/**
 * AuthZEN Request Correlation & Tracking
 * 
 * Implements X-Request-ID header per OpenID AuthZEN 1.0 Section 10.1.3
 * 
 * Spec Requirements:
 * - Requests MAY include X-Request-ID header
 * - PDP MUST echo the same ID in response
 * - Enables request tracing across client/server
 */

export interface CorrelationInfo {
	requestId: string;
	operationId: string;
	timestamp: number;
	resource?: string;
	action?: string;
}

export class CorrelationContext {
	private static requestMap = new Map<string, CorrelationInfo>();
	private static operationChains = new Map<string, string[]>();
	
	/**
	 * Generate secure random request ID
	 * 
	 * Uses crypto.randomUUID() for spec-compliant unique IDs
	 * 
	 * @returns {string} UUID v4 request ID
	 */
	static generateRequestId(): string {
		// Use crypto.randomUUID if available (modern browsers)
		if (typeof crypto !== 'undefined' && crypto.randomUUID) {
			return crypto.randomUUID();
		}
		
		// Fallback for older browsers
		return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
			const r = (Math.random() * 16) | 0;
			const v = c === 'x' ? r : (r & 0x3) | 0x8;
			return v.toString(16);
		});
	}
	
	/**
	 * Create correlation context for an operation
	 * 
	 * Tracks request IDs in a chain for multi-step operations
	 * 
	 * @param {string} operationId - Logical operation identifier
	 * @param {object} metadata - Optional metadata about the request
	 * @returns {string} Request ID for this specific request
	 * 
	 * @example
	 * ```typescript
	 * const requestId = CorrelationContext.createCorrelation('user-delete', {
	 *   resource: 'user:123',
	 *   action: 'delete'
	 * });
	 * ```
	 */
	static createCorrelation(
		operationId: string,
		metadata?: {
			resource?: string;
			action?: string;
		}
	): string {
		const requestId = this.generateRequestId();
		
		// Store correlation info
		this.requestMap.set(requestId, {
			requestId,
			operationId,
			timestamp: Date.now(),
			resource: metadata?.resource,
			action: metadata?.action
		});
		
		// Track in operation chain
		if (!this.operationChains.has(operationId)) {
			this.operationChains.set(operationId, []);
		}
		this.operationChains.get(operationId)!.push(requestId);
		
		// Cleanup old entries (keep last 1000)
		if (this.requestMap.size > 1000) {
			const oldestKey = this.requestMap.keys().next().value;
			if (oldestKey) {
				this.requestMap.delete(oldestKey);
			}
		}
		
		return requestId;
	}
	
	/**
	 * Get correlation info for a request ID
	 */
	static getCorrelation(requestId: string): CorrelationInfo | undefined {
		return this.requestMap.get(requestId);
	}
	
	/**
	 * Get all request IDs for an operation
	 * 
	 * Useful for debugging multi-step flows
	 */
	static getOperationChain(operationId: string): string[] {
		return this.operationChains.get(operationId) || [];
	}
	
	/**
	 * Validate response request ID matches sent ID
	 * 
	 * Per spec, server MUST echo the same request ID
	 */
	static validateResponseId(sentId: string, receivedId: string | null | undefined): boolean {
		if (!receivedId) {
			console.warn(
				`[AuthZ] Server did not return X-Request-ID header (sent: ${sentId}). ` +
				`PDP should echo request ID per OpenID AuthZEN Section 10.1.3`
			);
			return false;
		}
		
		if (sentId !== receivedId) {
			console.error(
				`[AuthZ] Request ID mismatch! Sent: ${sentId}, Received: ${receivedId}. ` +
				`This may indicate request/response correlation issues.`
			);
			return false;
		}
		
		return true;
	}
	
	/**
	 * Clear old correlation data (cleanup)
	 */
	static cleanup(olderThan: number = 60 * 60 * 1000): void {
		const cutoff = Date.now() - olderThan;
		
		for (const [requestId, info] of this.requestMap.entries()) {
			if (info.timestamp < cutoff) {
				this.requestMap.delete(requestId);
			}
		}
	}
	
	/**
	 * Get correlation statistics for debugging
	 */
	static getStats(): {
		totalRequests: number;
		activeOperations: number;
		oldestRequest: number | null;
	} {
		const timestamps = Array.from(this.requestMap.values()).map(i => i.timestamp);
		
		return {
			totalRequests: this.requestMap.size,
			activeOperations: this.operationChains.size,
			oldestRequest: timestamps.length > 0 ? Math.min(...timestamps) : null
		};
	}
}

// Automatic cleanup every hour
if (typeof window !== 'undefined') {
	setInterval(() => {
		CorrelationContext.cleanup();
	}, 60 * 60 * 1000);
}

